/* eslint-disable no-console, no-underscore-dangle, max-classes-per-file, class-methods-use-this */
/**
 * Configuración global de rendimiento para efectos Liquid Glass en Chatventas
 * Este archivo maneja la optimización automática basada en las capacidades del dispositivo.
 *
 * Reglas ESLint deshabilitadas a nivel de archivo:
 * - no-console: este módulo loguea info de performance al usuario (FPS bajo)
 * - no-underscore-dangle: convención interna para flags privados (_degradationLogged)
 * - max-classes-per-file: contiene PerformanceMonitor + GlassConfigManager juntos
 * - class-methods-use-this: getWebGLInfo es estática por diseño
 */

// Configuraciones de rendimiento por nivel
export const PERFORMANCE_LEVELS = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  DISABLED: 'disabled',
};

// Métricas para detectar capacidades del dispositivo
export const DEVICE_CAPABILITIES = {
  // Memoria RAM mínima recomendada (GB)
  MIN_MEMORY: 4,

  // Número mínimo de cores recomendados
  MIN_CORES: 4,

  // Resolución mínima para efectos completos
  MIN_RESOLUTION: 1920 * 1080,

  // Frame rate mínimo aceptable
  TARGET_FPS: 60,
};

// Configuraciones por nivel de rendimiento
export const PERFORMANCE_CONFIGS = {
  [PERFORMANCE_LEVELS.HIGH]: {
    enableLazyLoading: false,
    maxSimultaneousGlass: 50,
    useShaderMode: true,
    enableAllEffects: true,
    animationDuration: 300,
    quality: 1.0,
    settings: {
      displacementScale: 70,
      blurAmount: 0.1,
      aberrationIntensity: 2.5,
      mode: 'shader',
    },
  },

  [PERFORMANCE_LEVELS.MEDIUM]: {
    enableLazyLoading: true,
    maxSimultaneousGlass: 20,
    useShaderMode: false,
    enableAllEffects: true,
    animationDuration: 250,
    quality: 0.8,
    settings: {
      displacementScale: 56,
      blurAmount: 0.08,
      aberrationIntensity: 2,
      mode: 'standard',
    },
  },

  [PERFORMANCE_LEVELS.LOW]: {
    enableLazyLoading: true,
    maxSimultaneousGlass: 8,
    useShaderMode: false,
    enableAllEffects: false,
    animationDuration: 150,
    quality: 0.6,
    settings: {
      displacementScale: 32,
      blurAmount: 0.04,
      aberrationIntensity: 1,
      mode: 'standard',
      effect: 'flowingLiquid',
    },
  },

  [PERFORMANCE_LEVELS.DISABLED]: {
    enableLazyLoading: false,
    maxSimultaneousGlass: 0,
    useShaderMode: false,
    enableAllEffects: false,
    animationDuration: 0,
    quality: 0,
    settings: null,
  },
};

/**
 * Detector de capacidades del dispositivo
 */
export class DeviceCapabilityDetector {
  constructor() {
    this.capabilities = this.detectCapabilities();
    this.performanceLevel = this.determinePerformanceLevel();
  }

  /**
   * Detecta las capacidades del dispositivo actual
   */
  detectCapabilities() {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    return {
      // Capacidades básicas
      hasWebGL: !!gl,
      deviceMemory: navigator.deviceMemory || 4,
      hardwareConcurrency: navigator.hardwareConcurrency || 4,

      // Detección de tipo de dispositivo
      isMobile:
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ),
      isTablet: /iPad|Android.*(?!.*Mobile)/i.test(navigator.userAgent),

      // Información de pantalla
      screenResolution: window.screen.width * window.screen.height,
      pixelRatio: window.devicePixelRatio || 1,

      // Información de red (si está disponible)
      connection: navigator.connection
        ? {
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            saveData: navigator.connection.saveData,
          }
        : null,

      // Capacidades de WebGL si está disponible
      webglInfo: gl ? this.getWebGLInfo(gl) : null,
    };
  }

  /**
   * Obtiene información detallada de WebGL
   */
  getWebGLInfo(gl) {
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      vendor: debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : 'Unknown',
      renderer: debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : 'Unknown',
      version: gl.getParameter(gl.VERSION),
      maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      maxVertexAttributes: gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
    };
  }

  /**
   * Determina el nivel de rendimiento apropiado
   */
  determinePerformanceLevel() {
    const caps = this.capabilities;

    // Si no hay WebGL, deshabilitar efectos
    if (!caps.hasWebGL) {
      return PERFORMANCE_LEVELS.DISABLED;
    }

    // Si el usuario ha activado "save data", usar configuración baja
    if (caps.connection?.saveData) {
      return PERFORMANCE_LEVELS.LOW;
    }

    // Calcular puntuación basada en capacidades
    let score = 0;

    // Memoria RAM (peso: 30%)
    if (caps.deviceMemory >= 8) score += 30;
    else if (caps.deviceMemory >= 4) score += 20;
    else if (caps.deviceMemory >= 2) score += 10;

    // CPU cores (peso: 20%)
    if (caps.hardwareConcurrency >= 8) score += 20;
    else if (caps.hardwareConcurrency >= 4) score += 15;
    else if (caps.hardwareConcurrency >= 2) score += 10;

    // Resolución (peso: 20%)
    if (caps.screenResolution >= DEVICE_CAPABILITIES.MIN_RESOLUTION)
      score += 20;
    else if (caps.screenResolution >= 1366 * 768) score += 15;
    else score += 5;

    // Tipo de dispositivo (peso: 20%)
    if (!caps.isMobile && !caps.isTablet) score += 20;
    else if (caps.isTablet) score += 10;
    else score += 5;

    // Conexión (peso: 10%)
    if (caps.connection) {
      if (
        caps.connection.effectiveType === '4g' &&
        caps.connection.downlink > 10
      )
        score += 10;
      else if (caps.connection.effectiveType === '4g') score += 8;
      else if (caps.connection.effectiveType === '3g') score += 5;
    } else {
      score += 8; // Asumir buena conexión si no hay info
    }

    // Determinar nivel basado en puntuación
    if (score >= 80) return PERFORMANCE_LEVELS.HIGH;
    if (score >= 50) return PERFORMANCE_LEVELS.MEDIUM;
    if (score >= 25) return PERFORMANCE_LEVELS.LOW;
    return PERFORMANCE_LEVELS.DISABLED;
  }

  /**
   * Obtiene la configuración optimizada para el dispositivo actual
   */
  getOptimizedConfig() {
    return PERFORMANCE_CONFIGS[this.performanceLevel];
  }

  /**
   * Verifica si los efectos glass deben estar habilitados
   */
  shouldEnableGlass() {
    return this.performanceLevel !== PERFORMANCE_LEVELS.DISABLED;
  }
}

/**
 * Gestor de rendimiento global para efectos glass
 */
export class GlassPerformanceManager {
  constructor() {
    this.detector = new DeviceCapabilityDetector();
    this.activeGlassComponents = new Set();
    this.config = this.detector.getOptimizedConfig();
    this.frameMetrics = {
      lastFrameTime: performance.now(),
      frameTimes: [],
      averageFPS: 60,
    };

    this.startPerformanceMonitoring();
  }

  /**
   * Registra un componente glass activo
   */
  registerGlassComponent(componentId) {
    if (this.activeGlassComponents.size >= this.config.maxSimultaneousGlass) {
      console.warn(
        'ChatventasGlass: Máximo de componentes glass alcanzado, considerando lazy loading'
      );
      return false;
    }

    this.activeGlassComponents.add(componentId);
    return true;
  }

  /**
   * Desregistra un componente glass
   */
  unregisterGlassComponent(componentId) {
    this.activeGlassComponents.delete(componentId);
  }

  /**
   * Inicia el monitoreo de rendimiento
   */
  startPerformanceMonitoring() {
    const monitor = () => {
      const now = performance.now();
      const deltaTime = now - this.frameMetrics.lastFrameTime;

      this.frameMetrics.frameTimes.push(deltaTime);

      // Mantener solo los últimos 60 frames
      if (this.frameMetrics.frameTimes.length > 60) {
        this.frameMetrics.frameTimes.shift();
      }

      // Calcular FPS promedio
      const avgDeltaTime =
        this.frameMetrics.frameTimes.reduce((a, b) => a + b, 0) /
        this.frameMetrics.frameTimes.length;
      this.frameMetrics.averageFPS = 1000 / avgDeltaTime;

      // Ajustar configuración si el rendimiento es bajo
      if (this.frameMetrics.averageFPS < 30 && this.config.quality > 0.5) {
        this.degradePerformance();
      }

      this.frameMetrics.lastFrameTime = now;
      requestAnimationFrame(monitor);
    };

    requestAnimationFrame(monitor);
  }

  /**
   * Degrada el rendimiento automáticamente si es necesario
   */
  degradePerformance() {
    // Log only once per session to avoid console spam (this fires every time
    // FPS drops below 30 — many times in normal use). Pure informational.
    if (!this._degradationLogged) {
      console.info('[ChatventasGlass] reduciendo calidad por bajo FPS (<30)');
      this._degradationLogged = true;
    }
    this.config.quality *= 0.8;
    this.config.maxSimultaneousGlass = Math.max(
      1,
      Math.floor(this.config.maxSimultaneousGlass * 0.7)
    );

    // Notificar a todos los componentes activos sobre el cambio
    this.notifyPerformanceChange();
  }

  /**
   * Notifica a los componentes sobre cambios de rendimiento
   */
  notifyPerformanceChange() {
    window.dispatchEvent(
      new CustomEvent('chatventas-glass-performance-change', {
        detail: { config: this.config, fps: this.frameMetrics.averageFPS },
      })
    );
  }

  /**
   * Obtiene las métricas actuales de rendimiento
   */
  getPerformanceMetrics() {
    return {
      level: this.detector.performanceLevel,
      fps: this.frameMetrics.averageFPS,
      activeComponents: this.activeGlassComponents.size,
      maxComponents: this.config.maxSimultaneousGlass,
      quality: this.config.quality,
      capabilities: this.detector.capabilities,
    };
  }
}

// Instancia global del gestor de rendimiento
export const performanceManager = new GlassPerformanceManager();

// Exportar configuración actual
export const getCurrentConfig = () => performanceManager.config;
export const getDeviceCapabilities = () =>
  performanceManager.detector.capabilities;
export const shouldEnableGlass = () =>
  performanceManager.detector.shouldEnableGlass();
